import type {ReactNode} from 'react'
import {useTranslation} from 'react-i18next'
import {LockGlyph} from '@/components/ui/icons'
import {useReveal} from '@/hooks/common/use-reveal'
import {useCapabilities} from '@/hooks/empresa/organizacion/use-businesses'
import {rutaDeDescarga, useAbrirEvidencia, useUrlDeEvidencia} from '@/hooks/sgsst/evidencias/use-evidence'
import {cn} from '@/lib/utils'

/** Un clic: pide la URL firmada de ese archivo y lo abre en otra pestaña. */
export function DescargarEvidencia({
  ruta,
  reservado = false,
  className,
  title,
  testId,
  children,
}: {
  ruta: string
  // Documento de salud que quien lee no custodia (Res. 1843/2025): se ve que existe, sin control para abrirlo.
  reservado?: boolean
  className?: string
  title?: string
  testId?: string
  children?: ReactNode
}) {
  const {t} = useTranslation()
  const {abrir, isPending} = useAbrirEvidencia()
  if (reservado)
    return (
      <span
        className={cn(className, 'cursor-default text-muted-foreground hover:text-muted-foreground hover:no-underline')}
        data-testid={testId}
        title={t('evidence.soloCustodia')}>
        <LockGlyph className="mr-1 inline-block align-[-1px]" />
        {children}
        <span className="block text-[10px] font-normal whitespace-normal">{t('evidence.soloCustodia')}</span>
      </span>
    )
  return (
    <button
      type="button"
      className={cn('cursor-pointer text-left disabled:cursor-wait disabled:opacity-60', className)}
      title={title}
      data-testid={testId}
      disabled={isPending}
      aria-busy={isPending}
      onClick={() => abrir(ruta)}>
      {children}
    </button>
  )
}

/** Una evidencia que otra pantalla liga: se abre al clic y un documento de salud solo para quien custodia. */
export function EvidenciaLigada({
  businessId,
  evidencia,
  testId,
  className = 'text-primary hover:underline',
}: {
  businessId: string
  evidencia: {id: string; filename: string; de_salud: boolean}
  testId: string
  className?: string
}) {
  const custodiaSalud = useCapabilities(businessId).sgsst_custodiar_salud
  return (
    <DescargarEvidencia
      ruta={rutaDeDescarga(businessId, evidencia.id)}
      reservado={evidencia.de_salud && !custodiaSalud}
      testId={testId}
      className={className}>
      {evidencia.filename}
    </DescargarEvidencia>
  )
}

/** La URL se pide cuando la tarjeta entra en pantalla; hasta entonces el espacio queda reservado. */
export function MiniaturaDeEvidencia({ruta, alt}: {ruta: string; alt: string}) {
  const {ref, revealed} = useReveal<HTMLDivElement>('200px')
  const {data: url} = useUrlDeEvidencia(ruta, revealed)
  return (
    <div ref={ref} className="h-14 w-full bg-secondary" data-testid="evidencia-miniatura">
      {url && <img src={url} alt={alt} loading="lazy" className="h-14 w-full object-cover" />}
    </div>
  )
}
